import { MedplumClient } from "@medplum/core";

import { MEDPLUM_STORAGE_PREFIX } from "@/constants/medplum.constants";
import { ROUTES } from "@/constants/routes.constants";
import { isString } from "@/lib/utils";
import { activeProjectCache } from "@/lib/utils/helpers/auth-cache";

const FHIR_PATH = "/fhir/R4/";
const PROJECT_LIST_PATH = "/fhir/R4/Project";

/**
 * Holds the constructed client so `medplumFetch` can reference it without
 * touching the `medplumClient` const binding. The MedplumClient constructor
 * synchronously calls medplumFetch (via attemptResumeActiveLogin) when a stored
 * login exists, which is before the const is assigned — reading it directly
 * would throw a temporal-dead-zone ReferenceError.
 */
const clientRef: { current: MedplumClient | undefined } = {
  current: undefined,
};

const resolveRequestUrl = (input: Parameters<typeof fetch>[0]): string => {
  if (isString(input)) {
    return input;
  }

  if (input instanceof URL) {
    return input.toString();
  }

  return input.url;
};

const medplumFetch: typeof fetch = (input, init) => {
  const url = resolveRequestUrl(input);
  const superAdminProject = activeProjectCache.get();
  const isFhirCall = url.includes(FHIR_PATH);
  const isProjectList = url.endsWith(PROJECT_LIST_PATH);
  const isSuperAdmin = clientRef.current?.isSuperAdmin() ?? false;

  if (!isSuperAdmin || !superAdminProject || !isFhirCall || isProjectList) {
    return window.fetch(input, init);
  }

  const headers = new Headers(init?.headers);
  headers.set("X-Medplum-Project", superAdminProject);
  return window.fetch(input, { ...init, headers });
};

export const medplumClient = new MedplumClient({
  baseUrl: import.meta.env.VITE_PUBLIC_MEDPLUM_BASE_URL,
  fetch: medplumFetch,
  storagePrefix: MEDPLUM_STORAGE_PREFIX,
  onUnauthenticated: () => {
    window.location.assign(ROUTES.LOGIN);
  },
});

clientRef.current = medplumClient;
